import type { ItemFieldRow } from './item-field-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import type { FormulaVariableMetadata, FormulaVariableReaders } from './formula-variable-resolution';
import { readLayerScoringToken } from './layer-cell-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonFloat, roundPhpDecimal } from './php-decimal-values';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: ItemFieldRow,key: string): unknown => Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const trim = (value: string): string => value.replace(/^[\x00\x09\x0A\x0B\x0D\x20]+|[\x00\x09\x0A\x0B\x0D\x20]+(?![\s\S])/g,'');
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
export interface InventoryBaseSpeedMetadata extends FormulaVariableMetadata {
    speedTypes: readonly { readonly variable_name: string }[];
}
export interface InventoryBaseSpeedDependencies extends FormulaReadDependencies {
    metadata: InventoryBaseSpeedMetadata;
}
/** Actual default-derivation predicate: explicit zero is present. */
export function inventorySpeedLayersAbsent(row: ItemFieldRow,layer: string,readers: FormulaVariableReaders): boolean {
    if (!arrayLike(row)) throw new TypeError('Speed default predicate requires a PHP array');
    for (const value of Object.values(readers.layerEntries(row,layer))) {
        if (value === null) continue;
        const token = arrayLike(value) ? readLayerScoringToken(value) ?? '' : trim(phpJsonString(value));
        if (token !== '') return false;
    }
    return true;
}
/** Source inventory movement helper. Its entry gate is intentionally raw-root
 * only; canonical/provider reads begin after a root speed field opens it.
 * Derived speeds read the walking layer value, not its freshly scored sum.
 */
export function compileInventoryBaseSpeeds(input: ItemFieldRow,metadata: InventoryBaseSpeedMetadata,
    readers: FormulaVariableReaders): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Inventory base speeds require a PHP array');
    if (!metadata.speedTypes.some(speed=>phpJsonTruthy(own(input,speed.variable_name)))) return clone(input) as ItemFieldRow;
    const defaultSwimming = inventorySpeedLayersAbsent(input,'speed_swimming',readers);
    const defaultClimbing = inventorySpeedLayersAbsent(input,'speed_climbing',readers);
    const speeds: Record<string,unknown> = {};
    for (const speed of metadata.speedTypes) {
        const layer = speed.variable_name;
        const derived = layer === 'speed_swimming' ? defaultSwimming : layer === 'speed_climbing' ? defaultClimbing : null;
        let value: number;
        if (layer === 'speed_walking') value = scoreCarrierIntegerLayer(input,layer,0,true,metadata,readers);
        else if (derived === false) {
            const present = readers.layerValue(input,layer) !== null || phpJsonTruthy(readers.layerEntries(input,layer));
            value = present ? scoreCarrierIntegerLayer(input,layer,0,true,metadata,readers) : 0;
        } else if (derived === true) {
            let infinity = false;
            if (phpJsonTruthy(readers.layerEntries(input,layer))) {
                for (const entry of Object.values(readers.layerEntries(input,layer))) {
                    if (/^(?:inf|infinity|\{inf\}|\{infinity\})$/i.test(trim(phpJsonString(entry)))) { infinity = true; break; }
                }
            }
            value = infinity ? 99999 : readers.layerValue(input,'speed_walking') !== null
                ? roundPhpDecimal(phpJsonFloat(readers.layerValue(input,'speed_walking')) * 0.5,0) : 0;
        } else value = scoreCarrierIntegerLayer(input,layer,0,false,metadata,readers);
        Object.defineProperty(speeds,layer,{value:[Math.max(0,value)],enumerable:true,writable:true,configurable:true});
    }
    // Working object avoids named properties on an empty JS array disappearing
    // at JSON transport; final clone restores PHP list/map shape recursively.
    return clone({...input,base_speed_arr:speeds}) as ItemFieldRow;
}
export function createInventoryBaseSpeedCompiler(deps: InventoryBaseSpeedDependencies):
    (row: ItemFieldRow) => ItemFieldRow {
    const readers = createFormulaReadContext(deps);
    return row=>compileInventoryBaseSpeeds(row,deps.metadata,readers);
}
